"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getConfigUrl = exports.getInstance = exports.init = exports.offlineInit = exports.buildStorageKeySuffix = exports.EppoJSClient = exports.ChromeStorageEngine = void 0;
const js_client_sdk_common_1 = require("@eppo/js-client-sdk-common");
const assignment_cache_factory_1 = require("./cache/assignment-cache-factory");
const hybrid_assignment_cache_1 = require("./cache/hybrid-assignment-cache");
const configuration_factory_1 = require("./configuration-factory");
const sdk_data_1 = require("./sdk-data");
var chrome_storage_engine_1 = require("./chrome-storage-engine");
Object.defineProperty(exports, "ChromeStorageEngine", { enumerable: true, get: function () { return chrome_storage_engine_1.ChromeStorageEngine; } });
// Instantiate the configuration store with memory-only implementation.
const flagConfigurationStore = (0, configuration_factory_1.configurationStorageFactory)({
    forceMemoryOnly: true,
});
/**
 * Client for assigning experiment variations.
 * @public
 */
class EppoJSClient extends js_client_sdk_common_1.EppoClient {
    getStringAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getStringAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getStringAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getStringAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    /**
     * @deprecated Use getBooleanAssignment instead
     */
    getBoolAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        return this.getBooleanAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getBooleanAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getBooleanAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getBooleanAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getBooleanAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getIntegerAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getIntegerAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getIntegerAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getIntegerAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getNumericAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getNumericAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getNumericAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getNumericAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getJSONAssignment(flagKey, subjectKey, subjectAttributes, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getJSONAssignment(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getJSONAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getJSONAssignmentDetails(flagKey, subjectKey, subjectAttributes, defaultValue);
    }
    getBanditAction(flagKey, subjectKey, subjectAttributes, actions, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getBanditAction(flagKey, subjectKey, subjectAttributes, actions, defaultValue);
    }
    getBanditActionDetails(flagKey, subjectKey, subjectAttributes, actions, defaultValue) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getBanditActionDetails(flagKey, subjectKey, subjectAttributes, actions, defaultValue);
    }
    getExperimentContainerEntry(flagExperiment, subjectKey, subjectAttributes) {
        EppoJSClient.getAssignmentInitializationCheck();
        return super.getExperimentContainerEntry(flagExperiment, subjectKey, subjectAttributes);
    }
    static getAssignmentInitializationCheck() {
        if (!EppoJSClient.initialized) {
            js_client_sdk_common_1.applicationLogger.warn('Eppo SDK assignment requested before init() completed');
        }
    }
}
exports.EppoJSClient = EppoJSClient;
// Ensure that the client is instantiated during class loading.
// Use an empty memory-only configuration store until the `init` method is called,
// to avoid serving stale data to the user.
EppoJSClient.instance = new EppoJSClient(flagConfigurationStore, undefined, undefined, undefined, true);
EppoJSClient.initialized = false;
function buildStorageKeySuffix(apiKey) {
    // Note that we use the first 8 characters of the API key to create per-API key persistent storages and caches
    return apiKey.replace(/\W/g, '').substring(0, 8);
}
exports.buildStorageKeySuffix = buildStorageKeySuffix;
/**
 * Initializes the Eppo client with configuration parameters.
 *
 * The purpose is for use-cases where the configuration is available from an external process
 * that can bootstrap the SDK.
 *
 * This method should be called once on application startup.
 *
 * @param config - client configuration
 * @returns a singleton client instance
 * @public
 */
function offlineInit(config) {
    var _a, _b;
    const isObfuscated = (_a = config.isObfuscated) !== null && _a !== void 0 ? _a : false;
    const throwOnFailedInitialization = (_b = config.throwOnFailedInitialization) !== null && _b !== void 0 ? _b : true;
    try {
        const memoryOnlyConfigurationStore = (0, configuration_factory_1.configurationStorageFactory)({
            forceMemoryOnly: true,
        });
        memoryOnlyConfigurationStore
            .setEntries(config.flagsConfiguration)
            .catch((err) => js_client_sdk_common_1.applicationLogger.warn('Error setting flags for memory-only configuration store', err));
        EppoJSClient.instance.setFlagConfigurationStore(memoryOnlyConfigurationStore);
        // Allow the caller to override the default obfuscated mode, which is false
        // since the purpose of this method is to bootstrap the SDK from an external source,
        // which is likely a server that has not-obfuscated flag values.
        EppoJSClient.instance.setIsObfuscated(isObfuscated);
        if (config.assignmentLogger) {
            EppoJSClient.instance.setAssignmentLogger(config.assignmentLogger);
        }
        // There is no SDK key in the offline context.
        const storageKeySuffix = 'offline';
        // As this is a synchronous initialization,
        // we are unable to call the async `init` method on the assignment cache
        // which loads the assignment cache from the browser's storage.
        // Therefore, there is no purpose trying to use a persistent assignment cache.
        const assignmentCache = (0, assignment_cache_factory_1.assignmentCacheFactory)({
            storageKeySuffix,
            forceMemoryOnly: true,
        });
        EppoJSClient.instance.useCustomAssignmentCache(assignmentCache);
    }
    catch (error) {
        js_client_sdk_common_1.applicationLogger.warn('Eppo SDK encountered an error initializing, assignment calls will return the default value and not be logged');
        if (throwOnFailedInitialization) {
            throw error;
        }
    }
    EppoJSClient.initialized = true;
    return EppoJSClient.instance;
}
exports.offlineInit = offlineInit;
/**
 * Initializes the Eppo client with configuration parameters.
 * This method should be called once on application startup.
 * @param config - client configuration
 * @public
 */
async function init(config) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    js_client_sdk_common_1.validation.validateNotBlank(config.apiKey, 'API key required');
    let initializationError;
    const instance = EppoJSClient.instance;
    const { apiKey, persistentStore, baseUrl, maxCacheAgeSeconds, updateOnFetch, forceReinitialize } = config;
    try {
        if (EppoJSClient.initialized) {
            if (forceReinitialize) {
                js_client_sdk_common_1.applicationLogger.warn('Eppo SDK is already initialized, reinitializing since forceReinitialize is true.');
                EppoJSClient.initialized = false;
            }
            else {
                js_client_sdk_common_1.applicationLogger.warn('Eppo SDK is already initialized, skipping reinitialization since forceReinitialize is false.');
                return instance;
            }
        }
        // If any existing instances; ensure they are not polling
        instance.stopPolling();
        // Set up assignment logger and cache
        instance.setAssignmentLogger(config.assignmentLogger);
        // Default to obfuscated mode when requesting configuration from the server.
        instance.setIsObfuscated(true);
        const storageKeySuffix = buildStorageKeySuffix(apiKey);
        // Set the configuration store to the desired persistent store, if provided.
        // Otherwise, the factory method will detect the current environment and instantiate the correct store.
        const configurationStore = (0, configuration_factory_1.configurationStorageFactory)({
            maxAgeSeconds: maxCacheAgeSeconds,
            servingStoreUpdateStrategy: updateOnFetch,
            persistentStore,
            hasChromeStorage: (0, configuration_factory_1.hasChromeStorage)(),
            hasWindowLocalStorage: (0, configuration_factory_1.hasWindowLocalStorage)(),
        }, {
            chromeStorage: (0, configuration_factory_1.chromeStorageIfAvailable)(),
            windowLocalStorage: (0, configuration_factory_1.localStorageIfAvailable)(),
            storageKeySuffix,
        });
        instance.setFlagConfigurationStore(configurationStore);
        // instantiate and init assignment cache
        const assignmentCache = (0, assignment_cache_factory_1.assignmentCacheFactory)({
            chromeStorage: (0, configuration_factory_1.chromeStorageIfAvailable)(),
            storageKeySuffix,
        });
        if (assignmentCache instanceof hybrid_assignment_cache_1.default) {
            await assignmentCache.init();
        }
        instance.useCustomAssignmentCache(assignmentCache);
        // Set up parameters for requesting updated configurations
        const requestConfiguration = {
            apiKey,
            sdkName: sdk_data_1.sdkName,
            sdkVersion: sdk_data_1.sdkVersion,
            baseUrl,
            requestTimeoutMs: (_a = config.requestTimeoutMs) !== null && _a !== void 0 ? _a : undefined,
            numInitialRequestRetries: (_b = config.numInitialRequestRetries) !== null && _b !== void 0 ? _b : undefined,
            numPollRequestRetries: (_c = config.numPollRequestRetries) !== null && _c !== void 0 ? _c : undefined,
            pollAfterSuccessfulInitialization: (_d = config.pollAfterSuccessfulInitialization) !== null && _d !== void 0 ? _d : false,
            pollAfterFailedInitialization: (_e = config.pollAfterFailedInitialization) !== null && _e !== void 0 ? _e : false,
            pollingIntervalMs: (_f = config.pollingIntervalMs) !== null && _f !== void 0 ? _f : undefined,
            throwOnFailedInitialization: true,
            skipInitialPoll: (_g = config.skipInitialRequest) !== null && _g !== void 0 ? _g : false,
        };
        instance.setConfigurationRequestParameters(requestConfiguration);
        // We have two at-bats for initialization: from the configuration store and from fetching
        // We can resolve the initialization promise as soon as either one succeeds
        let initFromConfigStoreError = undefined;
        let initFromFetchError = undefined;
        const attemptInitFromConfigStore = configurationStore
            .init()
            .then(async () => {
            if (!configurationStore.getKeys().length) {
                // Consider empty configuration stores invalid
                js_client_sdk_common_1.applicationLogger.warn('Eppo SDK cached configuration is empty');
                initFromConfigStoreError = new Error('Configuration store was empty');
                return '';
            }
            const cacheIsExpired = await configurationStore.isExpired();
            if (cacheIsExpired && !config.useExpiredCache) {
                js_client_sdk_common_1.applicationLogger.warn('Eppo SDK set not to use expired cached configuration');
                initFromConfigStoreError = new Error('Configuration store was expired');
                return '';
            }
            return 'config store';
        })
            .catch((e) => {
            js_client_sdk_common_1.applicationLogger.warn('Eppo SDK encountered an error initializing from the configuration store', e);
            initFromConfigStoreError = e;
        });
        const attemptInitFromFetch = instance
            .fetchFlagConfigurations()
            .then(() => {
            return 'fetch';
        })
            .catch((e) => {
            js_client_sdk_common_1.applicationLogger.warn('Eppo SDK encountered an error initializing from fetching', e);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            initFromFetchError = e;
        });
        let initializationSource = await Promise.race([
            attemptInitFromConfigStore,
            attemptInitFromFetch,
        ]);
        if (!initializationSource) {
            // First attempt failed, but we have a second at bat that will be executed in the scope of the top-level try-catch
            if (!initFromConfigStoreError) {
                initializationSource = await attemptInitFromConfigStore;
            }
            else {
                initializationSource = await attemptInitFromFetch;
            }
        }
        if (!initializationSource) {
            // both failed, make the "fatal" error the fetch one
            initializationError = initFromFetchError;
        }
    }
    catch (error) {
        initializationError = error;
    }
    if (initializationError) {
        js_client_sdk_common_1.applicationLogger.warn('Eppo SDK was unable to initialize with a configuration, assignment calls will return the default value and not be logged' +
            (config.pollAfterFailedInitialization
                ? ' until an experiment configuration is successfully retrieved'
                : ''));
        if ((_h = config.throwOnFailedInitialization) !== null && _h !== void 0 ? _h : true) {
            throw initializationError;
        }
    }
    EppoJSClient.initialized = true;
    return instance;
}
exports.init = init;
/**
 * Used to access a singleton SDK client instance.
 * Use the method after calling init() to initialize the client.
 * @returns a singleton client instance
 * @public
 */
function getInstance() {
    return EppoJSClient.instance;
}
exports.getInstance = getInstance;
/**
 * Used to build the URL for fetching the flag configuration.
 * @returns a URL string
 * @public
 */
function getConfigUrl(apiKey, baseUrl) {
    const queryParams = { sdkName: sdk_data_1.sdkName, sdkVersion: sdk_data_1.sdkVersion, apiKey };
    return new js_client_sdk_common_1.ApiEndpoints({ baseUrl, queryParams }).ufcEndpoint();
}
exports.getConfigUrl = getConfigUrl;
//# sourceMappingURL=index.js.map